import React from 'react';
import { useToasts } from 'react-toast-notifications';
import { FiLayers } from 'react-icons/fi';

import useQuery from 'hooks/useQuery';
import studyService from 'services/studies';
import services from 'services/services';
import useMutation from 'hooks/useMutation';
import SelectModal from 'components/SelectModal';
import { STUDY_STATUS_ACTIVE } from 'utils/constants';
import { handleAddPeopleResponse } from 'utils/handleAddPeopleResponse';

/**
 * The modal for selecting a study to share study people to another study.
 *
 * @param {Object} param0 props
 * @param {() => void} param0.onClose A callback for when the modal closes
 * @param {string} param0.title The title of the modal
 * @param {any} param0.filterObject filter query object that contains the search query etc.
 * @param {boolean} param0.isOpen Whether the modal is open
 * @param {number} param0.sourceStudyId The id of the source study
 * @returns {ShareStudyPeopleModal} The component
 */
export function ShareStudyPeopleModal({ onClose, filterObject, isOpen, title, sourceStudyId }) {
    const { addToast } = useToasts();
    const { data: studies, isLoading: isStudiesLoading } = useQuery({
        queryFn: () => studyService.getStudiesSimple(),
        onError: (_, errorText) => {
            addToast(errorText, {
                appearance: 'error',
                autoDismiss: true
            });
        }
    });
    const { mutate: shareToStudy, isLoading: isSharing } = useMutation({
        mutationFn: async ({ destinationStudyId }) => {
            const response = await services.shareStudyPeople(sourceStudyId, destinationStudyId, filterObject);

            return { destinationStudyId, response };
        },
        onSuccess: ({ destinationStudyId, response }) => {
            handleAddPeopleResponse({
                response,
                studyId: destinationStudyId,
                addToast
            });
            onClose();
        },
        onError: (_, errorText) => {
            addToast(errorText, {
                appearance: 'error',
                autoDismiss: false
            });
        }
    });
    const { mutate: createStudy, isLoading: isStudyCreating } = useMutation({
        mutationFn: async ({ studyTitle }) =>
            services.createStudy({
                title: studyTitle,
                description: '',
                status: STUDY_STATUS_ACTIVE
            }),
        onSuccess: study => {
            shareToStudy({ destinationStudyId: study.id });
        },
        onError: (_, errorText) => {
            addToast(errorText, {
                appearance: 'error',
                autoDismiss: false
            });
        }
    });

    if (isStudiesLoading || !filterObject) return null;

    return (
        <SelectModal
            type="study"
            title={title}
            icon={<FiLayers />}
            items={studies}
            isOpen={isOpen}
            isLoading={isStudiesLoading || isSharing || isStudyCreating}
            onRequestClose={onClose}
            onSelect={destinationStudyId => shareToStudy({ destinationStudyId })}
            onCreate={studyTitle => createStudy({ studyTitle })}
        />
    );
}

export default ShareStudyPeopleModal;
